import {useCallback, useEffect, useRef, useState} from "react";
import styled from "styled-components";
import SearchIcon from "../icons/search.svg?react";
import ChevDownIcon from "../icons/chevron-down.svg?react";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import {appHolder} from "../../../utils/AppUtils";
import {KeyboardListener} from "../../../utils/interaction/key/KeyboardListener";
import {useAppStore} from "../../../StateManager";
import {DropdownButtonV5} from "../interaction/DropdownButtonV5";
import {HorizontalAlignment, VerticalAlignment} from "../../../utils/dom/DomUtils";
import {baseDistance, radius, zIndex} from "../styles/styles";
import {TextInputV5} from "../details/datatypes/TextInputV5";
import {SearchFieldStyled} from "../input/search/SearchField.styled";
import {colorPalette} from "../styles/colorPalette";
import {OrganizationWideSearchV5} from "./OrganizationWideSearchV5";
import type {ScopeWideSearchType} from "./OrganizationWideSearchUtils";
import {PortfolioWideSearchV5} from "./PortfolioWideSearchV5";

const MINIMUM_ORGANIZATION_SEARCH_LENGTH = 2;
const ORGANIZATION_SEARCH_TOO_SHORT_ERROR_MESSAGE = "Enter at least 2 characters to search";

interface ISearchParams {
	queryString: string;
	scope: ScopeWideSearchType;
	portfolioId?: string;
}

export const MainSearchV5 = () => {
	const appState = useAppStore((state) => state.appState);
	const [queryString, setQueryString] = useState<string>("");
	const onDetailsContainerClose = useAppStore((state) => state.appState.app.onDetailsContainerClose);
	const scopeWideSearchType = useAppStore((state) => state.scopeWideSearchType);
	const setScopeWideSearchType = useAppStore((state) => state.setScopeWideSearchType);
	const isSearchWindowOpen = useAppStore((state) => state.isScopeWideSearchOpen);
	const [committedSearchParams, setCommittedSearchParams] = useState<ISearchParams>({queryString, scope: scopeWideSearchType});
	const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
	const [hasAttemptedInvalidOrganizationSearch, setHasAttemptedInvalidOrganizationSearch] = useState<boolean>(false);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const setSearchWindowOpenState = useAppStore((state) => state.setIsScopeWideSearchOpen);
	const portfolioId = appState.relevantPortfolioId;
	const trimmedQueryString = queryString.trim();
	const isOrganizationSearchTooShort =
		scopeWideSearchType === "Organization" && trimmedQueryString.length > 0 && trimmedQueryString.length < MINIMUM_ORGANIZATION_SEARCH_LENGTH;
	const shouldShowInputErrorMessage = hasAttemptedInvalidOrganizationSearch && isOrganizationSearchTooShort;

	const triggerSearch = useCallback(() => {
		if (!trimmedQueryString) {
			setHasAttemptedInvalidOrganizationSearch(false);
			return;
		}

		if (isOrganizationSearchTooShort) {
			setHasAttemptedInvalidOrganizationSearch(true);
			requestAnimationFrame(() => {
				searchInputRef.current?.focus();
			});
			return;
		}

		setHasAttemptedInvalidOrganizationSearch(false);
		onDetailsContainerClose();
		setCommittedSearchParams({queryString: trimmedQueryString, scope: scopeWideSearchType, portfolioId});
		setSearchWindowOpenState(true);
		searchInputRef.current?.blur();
	}, [isOrganizationSearchTooShort, onDetailsContainerClose, portfolioId, scopeWideSearchType, setSearchWindowOpenState, trimmedQueryString]);

	const onInputPress = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === KeyboardListener.KEY_ENTER) {
				event.preventDefault();
				triggerSearch();
			}
		},
		[triggerSearch],
	);

	useEffect(() => {
		const onInputFocusIn = () => {
			setIsInputFocused(true);
		};

		const onInputFocusOut = () => {
			setIsInputFocused(false);
		};

		if (searchInputRef.current) {
			const input = searchInputRef.current;

			input.addEventListener("keydown", onInputPress);
			input.addEventListener("focusin", onInputFocusIn);
			input.addEventListener("focusout", onInputFocusOut);

			return () => {
				input.removeEventListener("keydown", onInputPress);
				input.removeEventListener("focusin", onInputFocusIn);
				input.removeEventListener("focusout", onInputFocusOut);
			};
		}
	}, [onInputPress]);

	const onSearchInput = useCallback((value: string) => {
		setQueryString(value);
		setHasAttemptedInvalidOrganizationSearch(false);
	}, []);

	const closeScopeWideSearch = useCallback(() => {
		setQueryString("");
		setHasAttemptedInvalidOrganizationSearch(false);
		setSearchWindowOpenState(false);
	}, [setSearchWindowOpenState]);

	useEffect(() => {
		if (!isSearchWindowOpen) {
			// eslint-disable-next-line react-hooks/set-state-in-effect -- query text is intentionally cleared when the search window closes.
			setQueryString("");
			setHasAttemptedInvalidOrganizationSearch(false);
		}
	}, [isSearchWindowOpen]);

	useEffect(() => {
		// eslint-disable-next-line react-hooks/set-state-in-effect -- invalid-search state intentionally resets when the search scope changes.
		setHasAttemptedInvalidOrganizationSearch(false);
	}, [scopeWideSearchType]);

	const navigationSignal = appState.app.navigation.signals.navigate;

	useEffect(() => {
		navigationSignal.add(closeScopeWideSearch);

		return () => {
			navigationSignal.remove(closeScopeWideSearch);
		};
	}, [navigationSignal, closeScopeWideSearch]);

	return (
		<MainSearchStyled
			$hasInputError={shouldShowInputErrorMessage}
			$isInputFocused={isInputFocused || isDropdownOpen}
		>
			<SearchFieldStyled
				className="SearchField"
				data-cy="portfolio-search"
			>
				<TextInputV5
					value={queryString}
					onInput={onSearchInput}
					divRef={searchInputRef}
					placeholder="Search"
					autoFocus={false}
					blurOnEnter={false}
					showErrorOnlyIfEdited={true}
					getErrorMessage={() => {
						if (shouldShowInputErrorMessage) {
							return ORGANIZATION_SEARCH_TOO_SHORT_ERROR_MESSAGE;
						}

						return "";
					}}
				/>
				<SearchIcon
					onClick={triggerSearch}
					className="searchIcon"
				/>
				<DropdownButtonV5
					onOpenChange={setIsDropdownOpen}
					button={
						<ScopeSelectorStyled>
							{`${scopeWideSearchType === "Portfolio" ? "Port." : "Org."}`}
							<ChevDownIcon
								width={12}
								height={12}
							/>
						</ScopeSelectorStyled>
					}
					options={[
						{
							label: "Portfolio",
							onClick: () => {
								setScopeWideSearchType("Portfolio");
								requestAnimationFrame(() => {
									searchInputRef.current?.focus();
								});
							},
							isActive: scopeWideSearchType === "Portfolio",
						},
						{
							label: "Organization",
							onClick: () => {
								setScopeWideSearchType("Organization");
								requestAnimationFrame(() => {
									searchInputRef.current?.focus();
								});
							},
							isActive: scopeWideSearchType === "Organization",
						},
					]}
					activeOption={[{label: scopeWideSearchType}]}
					horizontalAlignment={HorizontalAlignment.right}
					verticalAlignment={VerticalAlignment.bottomOuter}
					optionsZIndex={zIndex.overlayDetailsPanel + 1}
				/>
			</SearchFieldStyled>
			{isSearchWindowOpen && (
				<DomPortal destination={appHolder()}>
					{committedSearchParams.scope === "Portfolio" ? (
						<PortfolioWideSearchV5
							portfolioId={committedSearchParams.portfolioId}
							queryString={committedSearchParams.queryString}
							closeScopeWideSearch={closeScopeWideSearch}
						/>
					) : (
						<OrganizationWideSearchV5
							scopeWideSearchType={committedSearchParams.scope}
							queryString={committedSearchParams.queryString}
							closeScopeWideSearch={closeScopeWideSearch}
						/>
					)}
				</DomPortal>
			)}
		</MainSearchStyled>
	);
};

const ScopeSelectorStyled = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: ${baseDistance.sm};
	padding: ${baseDistance.xs};
	height: 100%;
	background-color: ${colorPalette.blueGray.c200Light};
	border-radius: 0 ${radius.sm} ${radius.sm} 0;
`;

const MainSearchStyled = styled.div<{$isInputFocused: boolean; $hasInputError: boolean}>`
	display: flex;
	align-items: center;
	flex-direction: row;

	${SearchFieldStyled} {
		border-color: ${(props) => {
			if (props.$hasInputError) {
				return colorPalette.negative.c500Primary;
			}

			return props.$isInputFocused ? colorPalette.primary.c500Primary : colorPalette.gray.c300;
		}};

		input {
			outline: none;
			border-top-right-radius: 0;
			border-bottom-right-radius: 0;

			&:focus-visible {
				outline: solid 1px ${(props) => (props.$hasInputError ? colorPalette.negative.c500Primary : colorPalette.primary.c500Primary)};
			}

			&::-webkit-search-cancel-button {
				right: 0;
			}
		}
	}
`;
